import React, { useState, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Chip,
    useTheme,
    SelectChangeEvent,
    OutlinedInput,
    IconButton,
    Collapse,
} from '@mui/material';
import { format, subDays, addMinutes } from 'date-fns';
import { logdbInstance } from '../axiosInstance';
import { DeviceInfo } from './types';
import { uptimeToMinutes, metricOptions } from './utils';
import DeviceCharts from './DeviceCharts';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface LabelChartsProps {
    processedDevices: DeviceInfo[];
    uniqueLabels: string[];
    isDarkMode: boolean;
}

const LabelCharts: React.FC<LabelChartsProps> = ({
    processedDevices,
    uniqueLabels,
    isDarkMode
}) => {
    const theme = useTheme();
    const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
    const [selectedMetric, setSelectedMetric] = useState<string>('loadUptime');
    const [deviceChartData, setDeviceChartData] = useState<{ [key: string]: any[] }>({});
    const [deviceLoadings, setDeviceLoadings] = useState<{ [key: string]: boolean }>({});
    const [devicesForCharts, setDevicesForCharts] = useState<DeviceInfo[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [chartsPerRow, setChartsPerRow] = useState<number>(3);
    const [timeRange, setTimeRange] = useState<number>(1);
    const [showDevices, setShowDevices] = useState(false);

    // Filtruj urządzenia na podstawie wybranych labelek
    const filteredDevicesByLabels = useMemo(() => {
        if (selectedLabels.length === 0) return [];
        
        return processedDevices.filter(device => {
            if (!device.label || device.label.length === 0) return false;
            
            const deviceLabelNames = device.label.map(l => l.label);
            return selectedLabels.some(selectedLabel => 
                deviceLabelNames.includes(selectedLabel)
            );
        });
    }, [processedDevices, selectedLabels]);

    // Funkcja do pobierania danych wykresów
    const fetchDeviceData = useCallback(async (boxNumber: string) => {
        try {
            setDeviceLoadings(prev => ({ ...prev, [boxNumber]: true }));

            const endDate = new Date();
            console.log('timeRange:', timeRange);
            // Odejmowanie milisekund zamiast subDays, by obsłużyć ułamki
            const startDate = new Date(endDate.getTime() - timeRange * 24 * 60 * 60 * 1000);
            const adjustedStartDate = startDate;
            console.log('startDate:', adjustedStartDate, 'endDate:', endDate);

            const response = await logdbInstance.get(`/log`, {
                params: {
                    box: `eq.box${boxNumber}`,
                    time: [
                        `gte.${format(adjustedStartDate, "yyyy-MM-dd'T'HH:mm:ss")}`,
                        `lt.${format(endDate, "yyyy-MM-dd'T'HH:mm:ss")}`
                    ]
                }
            });

            if (Array.isArray(response.data)) {
                const formattedData = response.data.map((item: any) => {
                    const adjustedTime = new Date(item.time);
                    adjustedTime.setHours(adjustedTime.getHours() + 2);
                    
                    return {
                        time: adjustedTime,
                        avg5mload: parseFloat(item.obj.avg5mload || '0'),
                        uptime: uptimeToMinutes(item.obj.uptime),
                        rawUptime: item.obj.uptime,
                        PM10: parseFloat(item.obj.PM10 || '0'),
                        PM25: parseFloat(item.obj.PM25 || '0'),
                        AMBIENT_TEMP: parseFloat(item.obj.AMBIENT_TEMP || '0'),
                        AMBIENT_HUMID: parseFloat(item.obj.AMBIENT_HUMID || '0'),
                        AMBIENT_PRESS: parseFloat(item.obj.AMBIENT_PRESS || '0'),
                        JAS_ABS: parseFloat(item.obj.JAS_ABS || '0'),
                        JAS_VALUE: parseFloat(item.obj.JAS_VALUE || '0'),
                        DUST_HEATER_NTC: parseFloat(item.obj.DUST_HEATER_NTC || '0'),
                        NH3_raw: parseFloat(item.obj.NH3_raw || '0'),
                        H2S_raw: parseFloat(item.obj.H2S_raw || '0'),
                        ODO: parseFloat(item.obj.ODO || '0'),
                        rssi: parseFloat(item.obj.rssi || '0'),
                        snr: parseFloat(item.obj.snr || '0'),
                        wifi_signal: parseFloat(item.obj.wifi_signal || '0'),
                    };
                });

                const fullRange = [];
                let currentDate = new Date(adjustedStartDate);
                
                while (currentDate <= endDate) {
                    fullRange.push({
                        time: new Date(currentDate),
                        avg5mload: null,
                        uptime: null,
                        rawUptime: null,
                        PM10: null,
                        PM25: null,
                        AMBIENT_TEMP: null,
                        AMBIENT_HUMID: null,
                        AMBIENT_PRESS: null,
                        JAS_ABS: null,
                        JAS_VALUE: null,
                        DUST_HEATER_NTC: null,
                        NH3_raw: null,
                        H2S_raw: null,
                        ODO: null,
                        rssi: null,
                        snr: null,
                        wifi_signal: null,
                    });
                    currentDate = addMinutes(currentDate, 5);
                }

                const mergedData = fullRange.map(rangeItem => {
                    const matchingItem = formattedData.find(dataItem =>
                        Math.abs(dataItem.time.getTime() - rangeItem.time.getTime()) < 150000
                    );
                    return matchingItem || rangeItem;
                });

                setDeviceChartData(prev => ({
                    ...prev,
                    [boxNumber]: mergedData
                }));
                setDeviceLoadings(prev => ({ ...prev, [boxNumber]: false }));
            }
        } catch (error: any) {
            console.error(`Error fetching data for box ${boxNumber}:`, error);
            setDeviceLoadings(prev => ({ ...prev, [boxNumber]: false }));
        }
    }, [timeRange]);

    // Obsługa zmiany wybranych labelek
    const handleLabelChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setSelectedLabels(typeof value === 'string' ? value.split(',') : value);
    };

    // Funkcja ładowania wykresów
    const handleLoadCharts = useCallback(async () => {
        if (filteredDevicesByLabels.length === 0) return;
        
        setIsLoading(true);
        setDevicesForCharts(filteredDevicesByLabels);
        
        // Wyczyść poprzednie dane wykresów
        setDeviceChartData({});
        
        // Załaduj dane dla wszystkich urządzeń z wybranymi labelkami
        const promises = filteredDevicesByLabels.map(device => 
            fetchDeviceData(device.box)
        );
        
        await Promise.all(promises);
        setIsLoading(false);
    }, [filteredDevicesByLabels, fetchDeviceData]);

    // Usuwanie labela z wybranych
    const handleDeleteLabel = (labelToDelete: string) => {
        setSelectedLabels(prev => prev.filter(label => label !== labelToDelete));
    };

    return (
        <Box sx={{ mt: 2 }}>
            {/* Kontrolki w Grid */}
            <Box 
                sx={{ 
                    position: 'sticky',
                    top: 20,
                    zIndex: 1000,
                    mb: 3,
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap'
                }}
            >
                {/* Kontrolki wymagające refetch */}
                <Paper 
                    sx={{ 
                        p: 2,
                        backdropFilter: 'blur(8px)',
                        backgroundColor: theme => theme.name === 'dark'
                            ? 'rgba(38, 38, 38, 0.9)' 
                            : 'rgba(255, 255, 255, 0.9)',
                        flex: '0 0 auto',
                        maxWidth: '50%',
                    }}
                    elevation={3}
                >
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Data Selection (requires reload)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <FormControl sx={{ width: 300 }}>
                            <InputLabel>Select Labels</InputLabel>
                            <Select
                                multiple
                                value={selectedLabels}
                                onChange={handleLabelChange}
                                input={<OutlinedInput label="Select Labels" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip 
                                                key={value} 
                                                label={value}
                                                size="small"
                                                onDelete={() => handleDeleteLabel(value)}
                                                onMouseDown={(event) => {
                                                    event.stopPropagation();
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                                size="small"
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            maxHeight: 300
                                        }
                                    }
                                }}
                            >
                                {uniqueLabels.map(label => (
                                    <MenuItem key={label} value={label}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ width: 150 }}>
                            <InputLabel>Time Range</InputLabel>
                            <Select
                                value={timeRange}
                                onChange={(e) => setTimeRange(Number(e.target.value))}
                                label="Time Range"
                                size="small"
                            >
                                <MenuItem value={1/24}>1 godzina</MenuItem>
                                <MenuItem value={2/24}>2 godziny</MenuItem>
                                <MenuItem value={0.25}>6 godzin</MenuItem>
                                <MenuItem value={0.5}>12 godzin</MenuItem>
                                <MenuItem value={1}>1 dzień</MenuItem>
                                <MenuItem value={3}>3 dni</MenuItem>
                                <MenuItem value={7}>1 tydzień</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            variant="contained"
                            onClick={handleLoadCharts}
                            disabled={selectedLabels.length === 0 || isLoading}
                            sx={{ minWidth: 120 }}
                        >
                            {isLoading ? 'Loading...' : 'Load Charts'}
                        </Button>
                    </Box>

                    {/* Informacje o wybranych urządzeniach */}
                    {selectedLabels.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            
                            {filteredDevicesByLabels.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                        {filteredDevicesByLabels.length} Devices:
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => setShowDevices(!showDevices)}
                                            sx={{ p: 0 }}
                                        >
                                            {showDevices ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                    </Box>
                                    <Collapse in={showDevices}>
                                        <Box 
                                            sx={{ 
                                                maxHeight: 60,
                                                overflowY: 'auto',
                                                overflowX: 'hidden',
                                                backgroundColor: theme => theme.palette.mode === 'dark' 
                                                    ? 'rgba(255, 255, 255, 0.05)' 
                                                    : 'rgba(0, 0, 0, 0.05)',
                                                border: theme => `1px solid ${theme.palette.divider}`,
                                                borderRadius: 1,
                                                p: 1,
                                                fontSize: '0.75rem',
                                                fontFamily: 'monospace',
                                                lineHeight: 1.2,
                                                wordWrap: 'break-word',
                                                whiteSpace: 'normal',
                                                mt: 1
                                            }}
                                        >
                                            {filteredDevicesByLabels.map(d => d.box).join(', ')}
                                        </Box>
                                    </Collapse>
                                </Box>
                            )}
                        </Box>
                    )}
                </Paper>

                {/* Kontrolki natychmiastowe */}
                {devicesForCharts.length > 0 && (
                    <Paper 
                        sx={{ 
                            p: 2,
                            backdropFilter: 'blur(8px)',
                            backgroundColor: theme => theme.name === 'dark'
                                ? 'rgba(38, 38, 38, 0.9)' 
                                : 'rgba(255, 255, 255, 0.9)',
                            flex: '0 0 auto',
                        }}
                        elevation={3}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Display Options (instant update)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <FormControl sx={{ width: 200 }}>
                                <InputLabel>Select Metric</InputLabel>
                                <Select
                                    value={selectedMetric}
                                    onChange={(e) => setSelectedMetric(e.target.value)}
                                    label="Select Metric"
                                    size="small"
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 300
                                            }
                                        }
                                    }}
                                >
                                    {metricOptions.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl sx={{ width: 150 }}>
                                <InputLabel>Charts per Row</InputLabel>
                                <Select
                                    value={chartsPerRow}
                                    onChange={(e) => setChartsPerRow(Number(e.target.value))}
                                    label="Charts per Row"
                                    size="small"
                                >
                                    <MenuItem value={1}>1</MenuItem>
                                    <MenuItem value={2}>2</MenuItem>
                                    <MenuItem value={3}>3</MenuItem>
                                    <MenuItem value={4}>4</MenuItem>
                                    <MenuItem value={6}>6</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Paper>
                )}
            </Box>

            {/* Wykresy */}
            {devicesForCharts.length > 0 ? (
                <DeviceCharts
                    processedDevices={devicesForCharts}
                    deviceChartData={deviceChartData}
                    deviceLoadings={deviceLoadings}
                    isDarkMode={isDarkMode}
                    selectedMetric={selectedMetric}
                    chartsPerRow={chartsPerRow}
                    timeRange={timeRange}
                />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 'calc(100vh - 300px)',
                        textAlign: 'center',
                    }}
                >
                    <Paper sx={{ p: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            No charts loaded
                        </Typography>
                        <Typography color="text.secondary">
                            Select labels from the dropdown above and click "Load Charts" to display charts for devices with those labels.
                        </Typography>
                    </Paper>
                </Box>
            )}
        </Box>
    );
};

export default LabelCharts; 